import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { LineChartComponent } from "@/features/dashboard/components/line-chart/line-chart.component";
import { ChartConfiguration, ChartOptions } from "chart.js";
import { ApiService } from "@/modules//dashboard/infra/api.service";
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { DashboardData, DashboardResponse } from "@/modules//dashboard/core/types";

@Component({
  selector: 'app-line-items',
  standalone: true,
  imports: [
    LineChartComponent,
    NzButtonModule,
    NzIconModule,
    NzToolTipModule
  ],
  templateUrl: './line-items.component.html',
  styleUrl: './line-items.component.scss'
})
export class LineItemsComponent implements OnInit {

  @ViewChild(LineChartComponent) lineChart?: LineChartComponent;
  public chartOptions!: ChartOptions
  public chartData!: ChartConfiguration['data']
  public loading: boolean = false

  private apiData: DashboardData[] = []

  @Input() limit: number = 6;
  @Input() expanded: boolean = false;
  @Input() isMonitorMode: boolean = false;

  constructor(private dashboardService: ApiService) {}

  ngOnInit() {
    this.getApiChartData();
  }

  getApiChartData() {
    this.loading = true
    this.dashboardService.findAll().subscribe({
      next: (data: DashboardResponse<DashboardData>) => {
        this.apiData = data.items
        this.setChartData(this.apiData)
        this.setChartOptions();
        this.loading = false
      },
      error: (err: Error) => {
        err.message
        this.loading = false
      }
    })
  }

  setChartOptions(): void {
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {title: {display: true, text: ''}, grid: {display: false}},
        y: {title: {display: true, text: ''}, grid: {display: true}}
      },
      plugins: {
        legend: {position: 'bottom'}
      }
    }
  }

  setChartData(apiData: DashboardData[]) {
    const mesesPtBr = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];

    const hoje = new Date();
    const ultimosMeses: { ano: number, mes: number }[] = [];

    // Últimos 6 meses
    for (let i = this.limit - 1; i >= 0; i--) {
      const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
      ultimosMeses.push({ ano: data.getFullYear(), mes: data.getMonth() });
    }

    // Agrupar dados por Empresa -> Mês -> Contagem
    const companyData: Record<string, Record<string, number>> = {};

    apiData.forEach(item => {
      const dataItem = new Date(item.formatted_date);
      const chaveMes = `${dataItem.getFullYear()}-${String(dataItem.getMonth() + 1).padStart(2, '0')}`;
      const companyName = item.company_name;

      // Verifica se o mês está dentro dos últimos 6 meses
      const isRelevantMonth = ultimosMeses.some(({ ano, mes }) => 
        ano === dataItem.getFullYear() && mes === dataItem.getMonth()
      );

      if (isRelevantMonth) {
        if (!companyData[companyName]) {
          companyData[companyName] = {};
          // Inicializa todos os meses com 0 para esta empresa
          ultimosMeses.forEach(({ ano, mes }) => {
             const k = `${ano}-${String(mes + 1).padStart(2, '0')}`;
             companyData[companyName][k] = 0;
          });
        }
        companyData[companyName][chaveMes]++;
      }
    });

    const labels = ultimosMeses.map(({ ano, mes }) => `${mesesPtBr[mes]}/${ano}`);
    
    const datasets = Object.keys(companyData).map((companyName, index) => {
      const data = ultimosMeses.map(({ ano, mes }) => {
        const k = `${ano}-${String(mes + 1).padStart(2, '0')}`;
        return companyData[companyName][k];
      });

      const color = this.getColor(index);

      return {
        data: data,
        label: companyName,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: false,
        tension: 0.4 // Curva suave
      };
    });

    this.chartData = {
      labels: labels,
      datasets: datasets
    };
  }

  getColor(index: number): string {
    const colors = [
      '#00869e', // Teal
      '#c0142c', // Red
      '#ffad1f', // Orange/Yellow
      '#7cb342', // Green
      '#8e24aa', // Purple
      '#039be5', // Light Blue
      '#e53935', // Another Red
      '#fdd835', // Yellow
      '#43a047', // Another Green
      '#5e35b1'  // Deep Purple
    ];
    return colors[index % colors.length];
  }

  public resizeChart(): void {
    this.lineChart?.resizeChart();
  }

  public exportChart(): void {
    if (this.lineChart && this.lineChart.chart && this.lineChart.chart.chart) {
      const url = this.lineChart.chart.chart.toBase64Image();
      const link = document.createElement('a');
      link.download = 'Check-lists por Empresas.png';
      link.href = url;
      link.click();
    }
  }
}
